import {
  getDefinitionNameByCode,
  getFieldNameByCode,
  getInstanceNameByCode,
} from './mapper'

import axios from 'axios'
import { uniq } from 'lodash'
import { v4 as uuid } from 'uuid'
import { writable } from 'svelte/store'
import firebase from 'firebase/compat/app'
import 'firebase/compat/app-check'

const { NEXT_PUBLIC_HOST } = __myapp

export const API_URL =
  NEXT_PUBLIC_HOST || 'https://europe-west2-synq-functions.cloudfunctions.net/app'

/*
 * App Check is enforced on the backend API, so requests without this header
 * are rejected. The SDK caches the token and refreshes it in the background,
 * so this is only a network call when the cache is cold. Returns undefined
 * (and lets the API reject the request with its own error) if App Check was
 * never activated, e.g. the site key env var is missing.
 */
export const getAppCheckToken = async () => {
  try {
    const { token: appCheckToken } = await firebase.appCheck().getToken()
    return appCheckToken
  } catch (error) {
    console.error('App Check token could not be obtained:', error)
    return undefined
  }
}

// Helper function to make authenticated API calls with automatic token refresh retry
export const makeAuthenticatedRequest = async (requestConfig, retryCount = 0) => {
  let accessToken
  const unsubscribe = token.subscribe((token) => {
    accessToken = token
  })
  unsubscribe()

  try {
    return await axios({
      ...requestConfig,
      headers: {
        ...requestConfig.headers,
        authorization: accessToken,
        'X-Firebase-AppCheck': await getAppCheckToken(),
      },
    })
  } catch (error) {
    // If token expired and we haven't retried yet, refresh token and retry
    if ((error.response?.status === 401 || error.response?.status === 403) && retryCount === 0) {
      try {
        const currentUser = firebase.auth().currentUser
        if (currentUser) {
          const freshToken = await currentUser.getIdToken(true) // Force refresh
          token.set(`Bearer ${freshToken}`)
          
          // Retry the request with the new token
          return await makeAuthenticatedRequest(requestConfig, retryCount + 1)
        }
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError)
        throw error
      }
    }
    throw error
  }
}

export const globalInterval = writable(null)

export const messageSettings = writable({
  notification_title: '',
  notification_body: '',
  call_centre_greeting: '',
  agent_from_number: '',
  agent_name: '',
  reference_id: '',
  reference_id_label: '',
  call_centre_submit_button: '',
  call_centre_cancel_button: '',
})

export const pingTimer = writable(null)

export const fbAuth = writable(null)

export const provider = writable(null)

export const users = writable([])

export const collectionPoints = writable([])

export const activeUserTabIndex = writable(0)

export const token = writable('')

export const providerName = writable('')

export const pingTimers = writable({})

export const fieldsDictionary = writable([])

export const tabName = writable('Customer 1')

export const deleteUser = async (id) => {
  await makeAuthenticatedRequest({
    method: 'delete',
    url: `${API_URL}/customer_connections/${id}`,
  })
}

export const getFieldsDictionary = async () => {
  const {
    data: { data },
  } = await makeAuthenticatedRequest({
    method: 'get',
    url: `${API_URL}/dictionary/fields`,
  })

  return data
}

export const createNewConnection = async (user, id) => {
  let settings

  const unsubscribe = messageSettings.subscribe((data) => (settings = data))
  unsubscribe()

  const newId = uuid()
  await makeAuthenticatedRequest({
    method: 'put',
    url: `${API_URL}/customer_connections`,
    data: {
      code: id || newId,
      phone: user.phone,
      reference_id: user.reference_id,
      collection_point: user.collectionPoint,
      settings: user.settings || settings,
    },
  })
}

export const updateConnection = async (user, id) => {
  await makeAuthenticatedRequest({
    method: 'post',
    url: `${API_URL}/customer_connections/${user.id}`,
    data: {
      phone: user.phone,
      collection_point: user.collectionPoint,
    },
  })
}

export const getConnectionById = async (id) => {
  const { data } = await makeAuthenticatedRequest({
    method: 'get',
    url: `${API_URL}/customer_connections/${id}`,
  })

  return data
}

export const getConnectionFields = async (id) => {
  const { data } = await makeAuthenticatedRequest({
    method: 'get',
    url: `${API_URL}/customer_connections/fields/${id}`,
  })

  return data
}

export const getConnectionData = async (id) => {
  const { data } = await makeAuthenticatedRequest({
    method: 'get',
    url: `${API_URL}/customer_connections/${id}`,
  })

  return data
}

export const updateUser = (updateIndex, userToUpdate) => {
  users.update((users) =>
    users.map((user, idx) => {
      if (idx === updateIndex) {
        return userToUpdate
      }

      return user
    })
  )
}

export const closeUser = async (index) => {
  let points
  let providerData

  const unsubscribe1 = collectionPoints.subscribe((_points) => {
    points = _points
  })
  unsubscribe1()

  const unsubscribe2 = provider.subscribe((value) => (providerData = value))
  unsubscribe2()

  users.update((users) => {
    const user = users[index]

    if (!user?.id) {
      return users
    }

    if (!user.isNew) {
      deleteUser(user.id)
    }

    // set new empty user if deleting the last user
    if (users.length === 1) {
      messageSettings.set(points[0]?.settings || providerData?.settings)
      users[0] = {
        ...users[0],
        id: uuid(),
        // default collection point
        collectionPoint: points[0]?.code,
        isCancelled: false,
        error: null,
        fields: null,
        phone: '',
        isPending: false,
        isNew: true,
        settings: {},
        userTitle: null,
        reference_id: null,
        next_template: {},
      }

      return users
    } else {
      messageSettings.set(points[0]?.settings || providerData?.settings)
      return users.filter((_, idx) => idx !== index)
    }
  })
}


// The collection point's own `fields[]` rows are saved by gmd-provider-web in
// the order the provider sees them (template slot, then collection, then tag,
// then field definition), but a submission arrives in whatever order the app
// sent it. Each level below is ordered by the first config row it matches;
// anything the config doesn't mention keeps its arrival order, after the rest.
const UNTAGGED_KEY = 'instance/default'

const rowTag = (row) => row.tags?.[0]?.urn || UNTAGGED_KEY
const submittedTag = (v) => v.instance?.tags?.[0]?.urn || UNTAGGED_KEY

const configIndex = (rows, matches) => {
  const index = rows.findIndex(matches)
  return index === -1 ? Infinity : index
}

const sortByConfig = (items, rank) =>
  items
    .map((item, arrival) => ({ item, arrival, rank: rank(item) }))
    .sort((a, b) => (a.rank === b.rank ? a.arrival - b.arrival : a.rank - b.rank))
    .map(({ item }) => item)

export const mapFields = (dictionary, fields, collectionPointFields = []) => {
  const templateGroups = {}
  fields.forEach((v) => {
    const templateCode = v.template?.slot_id || v.template?.dictionary_urn || 'default'
    if (!templateGroups[templateCode]) {
      templateGroups[templateCode] = { template: v.template, items: [] }
    }
    templateGroups[templateCode].items.push(v)
  })

  const isSlot = (templateCode) => (f) =>
    f.slot_id === templateCode || f.template === templateCode

  return sortByConfig(Object.entries(templateGroups), ([templateCode]) =>
    configIndex(collectionPointFields, isSlot(templateCode))
  ).map(([templateCode, group]) => {
    const templateField = collectionPointFields.find(isSlot(templateCode))
    const templateLabel = templateField?.template_custom_name || group.template?.name

    return {
      templateLabel,
      collections: mapCollectionFields(
        dictionary,
        group.items,
        collectionPointFields.filter(isSlot(templateCode))
      ),
    }
  })
}

// Groups a (single slot's) flat submitted-field list by collection, then by
// instance. Split out of mapFields so the new slot-grouping layer above it
// can reuse this unchanged per slot. `slotRows` is that slot's share of the
// collection point's `fields[]` config, which sets the order at every level.
const mapCollectionFields = (dictionary, fields, slotRows = []) => {
  const columns = sortByConfig(
    uniq(fields.map((f) => f.collection.dictionary_urn)),
    (col) => configIndex(slotRows, (row) => row.field === col)
  )

  const item = {}
  columns.forEach((col) => {
    item[col] = fields.filter((cus) => cus.collection.dictionary_urn === col)
  })

  const result = Object.entries(item).map(([collectionUrn, fieldValues]) => {
    const instancesIds = uniq(fieldValues.map((v) => v.instance.id))

    const instancesArr = []
    instancesIds.forEach((ins) => {
      const res = fieldValues.filter((v) => v.instance.id === ins)
      instancesArr.push(res)
    })

    const isInstance = (fld) => (row) =>
      row.field === collectionUrn && rowTag(row) === submittedTag(fld[0])

    const rst = []
    sortByConfig(instancesArr, (fld) =>
      configIndex(slotRows, isInstance(fld))
    ).forEach((fld) => {
      const rows = slotRows.filter(isInstance(fld))
      const orderedFields = sortByConfig(fld, (f) =>
        configIndex(rows, (row) => row.field_definition === f.field.dictionary_urn)
      )
      const dictionaryEntry = dictionary.find(
        (f) => f.code === fld[0].collection.dictionary_urn
      )
      const tagUrn = fld[0].instance.tags?.[0]?.urn
      const instanceName =
        tagUrn && dictionaryEntry
          ? getInstanceNameByCode(dictionaryEntry, tagUrn)
          : 'DEFAULT'

      rst.push({
        id: uuid(),
        name: instanceName,
        fields: orderedFields.map((f) => ({
          id: uuid(),
          field_definition: getDefinitionNameByCode(
            dictionaryEntry,
            f.field.dictionary_urn
          ),
          value: f.value,
        })),
      })
    })

    return {
      name: getFieldNameByCode(dictionary, collectionUrn),
      data: rst,
    }
  })

  return result
}

export const fakeFields = () => [
  {
    field: 'NAME',
    instance: 'DEFAULT',
    field_definition: 'GIVEN_NAME',
    instanceValue: 'GIVEN_NAME',
    value: 'John',
  },
  {
    field: 'NAME',
    instance: 'DEFAULT',
    field_definition: 'FAMILY_NAME',
    instanceValue: 'FAMILY_NAME',
    value: 'Appleseed',
  },
  {
    field: 'EMAIL',
    instance: 'WORK',
    field_definition: 'EMAIL',
    instanceValue: 'EMAIL',
    value: 'John-Appleseed@mac.com',
  },
  {
    field: 'EMAIL',
    instance: 'SCHOOL',
    field_definition: 'EMAIL',
    instanceValue: 'EMAIL',
    value: 'gb.s@c.com',
  },
  {
    field: 'DRIVING_LICENCE',
    instance: 'MAIN',
    field_definition: 'NUMBER',
    instanceValue: 'NUMBER',
    value: 'Cdsc',
  },
  {
    field: 'DRIVING_LICENCE',
    instance: 'MAIN',
    field_definition: 'EXPIRY_DATE',
    instanceValue: 'EXPIRY_DATE',
    value: '2022-06-08T16:48:01.451Z',
  },
  {
    field: 'DRIVING_LICENCE',
    instance: 'MAIN',
    field_definition: 'ISSUE_DATE',
    instanceValue: 'ISSUE_DATE',
    value: '2022-06-08T16:48:05.381Z',
  },
  {
    field: 'DRIVING_LICENCE',
    instance: 'MAIN',
    field_definition: 'COUNTRY_OF_ISSUE',
    instanceValue: 'COUNTRY_OF_ISSUE',
    value: 'United Kingdom',
  },
  {
    field: 'COMPANY',
    instance: 'DEFAULT',
    field_definition: 'COMPANY_NAME',
    instanceValue: 'COMPANY_NAME',
    value: 'Dscs',
  },
]
